import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators, ValidationErrors } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MediaObserver } from '@angular/flex-layout';
import { combineLatest, of, Subject } from 'rxjs';
import { first, map, take, takeUntil } from 'rxjs/operators';

import { ApplicationService, SchoolGroupInfo, SchoolInfo } from 'services/application/application.service';
import { AwardsService, AwardProgramInfo } from 'services/awards/awards.service';
import { DialogService } from 'services/dialogs/dialogs.service';
import { ErrorService } from 'services/error/error.service';
import { SplashService } from 'services/splash/splash.service';
import { UserInfo, UserService } from 'services/user/user.service';
import { SettingsService } from 'services/settings/settings.service';

@Component({
    selector: 'app-manage-schoolgroups',
    styleUrls: ['schoolgroups.component.scss'],
    templateUrl: 'schoolgroups.component.html'
})

export class ManageSchoolGroupsComponent implements OnInit, OnDestroy {
    @ViewChild('paginator') paginator: MatPaginator;
    @ViewChild(MatSort) sorter: MatSort;

    private _subrelease: Subject<void> = new Subject<void>();

    AllHeaders: string[] = ['SchoolGroupID', 'Name', 'Schools', 'Actions'];
    AvailableSchools: SchoolInfo[];
    AwardInfo: AwardProgramInfo;
    CurrentSchoolYear: string;
    SchoolGroups: SchoolGroupInfo[];
    NoData: boolean;
    ViewReady: boolean;
    ShowingGroupPanel: boolean;
    SmallDisplay: boolean;
    GroupsData: MatTableDataSource<SchoolGroupInfo>;
    GroupEntryForm: FormGroup;
    User: UserInfo;

	constructor(private appService: ApplicationService, private awardService: AwardsService, private settingsSvc: SettingsService,
        private dialogService: DialogService, private errorService: ErrorService, private fb: FormBuilder,
        private mediaChange: MediaObserver, private splashService: SplashService, private userService: UserService) {

    }

    ngOnInit() {
        const self = this;

        self.AvailableSchools = null;
        self.CurrentSchoolYear = null;
        self.SchoolGroups = null;
        self.NoData = false;
        self.User = null;
        self.ViewReady = false;
        self.ShowingGroupPanel = false;
        self.GroupsData = new MatTableDataSource<SchoolGroupInfo>([]);

        self.SmallDisplay = (self.mediaChange.isActive('xs') || self.mediaChange.isActive('sm'));

        self.mediaChange.media$.pipe(takeUntil(self._subrelease)).subscribe(media => {
            self.SmallDisplay = (media.mqAlias === 'xs' || media.mqAlias === 'sm');
        });

        // Get the first non-null UserInfo object so we can begin setting up the component
        self.userService.UserSubject.pipe(
            takeUntil(self._subrelease),
            first(user => user && !!user.AuthToken)
        ).subscribe(user => {
            // Set up constant monitoring of the user object, to catch AuthToken updates.
            self.userService.UserSubject.pipe(takeUntil(self._subrelease)).subscribe(_user => { self.User = _user; });

			self.settingsSvc.CurrentSchoolYearSubject.pipe(takeUntil(self._subrelease)).subscribe(year => {
				self.CurrentSchoolYear = year;

				// Load static data for use in the compoment.
				const curAwardObs = self.awardService.CurrentAwardsSub;
				const availSchoolsObs = self.appService.GetAllSchools(self.CurrentSchoolYear);

				combineLatest(curAwardObs, availSchoolsObs).pipe(
					takeUntil(self._subrelease),
					map(packet => {
						return {
							CurrentAward: packet[0],
							AvailableSchools: packet[1]
						};
					})
				).subscribe(packet => {
					self.AvailableSchools = packet.AvailableSchools;
					self.AwardInfo = packet.CurrentAward;

					// Finish loading page info
					self.appService.GetAllSchoolGroups(self.AwardInfo.AwardID, self.CurrentSchoolYear).pipe(
						takeUntil(self._subrelease)
					).subscribe(groups => {
						self.SchoolGroups = groups;

						// Setup the form group
						self.GroupEntryForm = self.fb.group({
							SchoolGroupID: [null],
							OriginalName: [null],
							Name: [null, [Validators.required, Validators.maxLength(150)]],
							Schools: self.fb.array([]),
							SchoolYear: [self.CurrentSchoolYear]
						}, { validator: SchoolGroupValidator });

						// Update the data source
						self.GroupsData.data = self.SchoolGroups;
						self.GroupsData.sort = self.sorter;
						self.GroupsData.paginator = self.paginator;

						self.ViewReady = true;
					}, error => self.errorService.HandleServiceError(error));
				}, error => self.errorService.HandleServiceError(error));
			});
        });
    }

    ngOnDestroy() {
        this._subrelease.next(); this._subrelease.complete();
    }

    AddGroupCancelHandler() {
        const self = this;

        // Null out the form, then close the panel.
        self.GroupEntryForm.reset();

        self.ShowingGroupPanel = false;
    }

    AddGroup() {
        const self = this;

        const form = self.GroupEntryForm.getRawValue();

        if (!form || !self.GroupEntryForm.valid) { return; }

        // Find the group leader and set it.
        const leadLocationObj = form.Schools.find(item => item.IsGroupLeader === true);
        const leadLocation = leadLocationObj && leadLocationObj.LocationNumber;

        // Get a confirmation before proceeding
        let msg = `You are about to add the following new school group:<br /><br />
        <strong>Group Name: </strong>${form.Name}<br />
        <strong>Schools: </strong><br /><ul>`;

        if (form.Schools && !!form.Schools.length) {
            form.Schools.forEach(item => {
                msg += `<li>${(item.IsGroupLeader ? '<strong>' : '')}${item.LocationName} (${item.LocationNumber})` +
                `${(item.IsGroupLeader === true ? ' (Group Leader)' : '')}${(item.IsGroupLeader ? '</strong>' : '')}</li>`;
            });
        }

        msg += `</ul><br /><br />Once the school group update is processed, it will take effect immediately. Any ` +
        `future nominations for this group will use the location #${leadLocation} as the school number.<br /><br />` +
        `Are you sure you wish to add this new school group as shown above?`;

        self.dialogService.OpenConfirmationDialog({
            TitleText: 'Confirmation Requested',
            MessageText: msg,
            OKText: 'Yes - Add School Group',
            CancelText: 'No - CANCEL'
        }).pipe(takeUntil(self._subrelease)).subscribe(
            result => {
                if (result === true) {
                    // User has opted to proceed. Signal the system to add the school group.
                    const dbPacket = {
                        LeadLocationNumber: leadLocation,
                        Name: form.Name,
                        SchoolYear: self.CurrentSchoolYear,
                        Schools: form.Schools.map(item => {
                            return {
                                LocationNumber: item.LocationNumber,
                                IsGroupLeader: item.IsGroupLeader
                            };
                        })
                    };

					self.appService.AddSchoolGroup(self.User.AuthToken, self.User.CurrentRole, self.AwardInfo.AwardID, dbPacket, self.CurrentSchoolYear)
                        .pipe(takeUntil(self._subrelease)).subscribe(groups => {
                            // Toast the success
                            self.splashService.SnackbarSuccess('School group added successfully.');

                            // Update the list of known school groups
                            self.SchoolGroups = groups;
                            self.GroupsData.data = groups;

                            // Reset the term form
                            self.GroupEntryForm.reset();

                            // Hide the panel
                            self.ShowingGroupPanel = false;
                        }, error => {
                            self.errorService.HandleServiceError(error);
                        });
                }
            }
        );
    }

    AddSchool() {
        const self = this;

        if (!self.GroupEntryForm) { return; }

        const scharr = <FormArray>self.GroupEntryForm.get('Schools');

        // Add a new entry to the form array for the schools
        const newschool = self.fb.group({
            LocationName: [''],
            LocationNumber: [''],
            SchoolGroupSchoolID: [null],
            IsGroupLeader: [false],
            SortOrder: [(scharr.length)],
            SchoolInfo: [null],
            PendingRemove: [false]
        });

        scharr.push(newschool);
    }

    ReinstateSchool(schoolgp: FormGroup) {
        const self = this;

        if (!schoolgp) { return; }

        const school = schoolgp.value;

        if (!school || !school.PendingRemove) { return; }

        // Get a confirmation before proceeding.
        const msg = `You are about to reinstate the following school to this school group:<br /><br />` +
        `<strong>Location Name: </strong>${school.LocationName}<br />` +
        `<strong>Location Number: </strong>${school.LocationNumber}<br /><br />` +
        `Are you sure you wish to reinstate this school in the group?`;

        self.dialogService.OpenConfirmationDialog({
            TitleText: 'Confirmation Requested',
            MessageText: msg,
            OKText: 'Yes - Reinstate',
            CancelText: 'No - CANCEL'
        }).pipe(takeUntil(self._subrelease)).subscribe(result => {
            if (result === true) {
                // User has elected to proceed. Clear the pending delete.
                schoolgp.get('PendingRemove').setValue(false);
            }
        });
    }

    RemoveGroup(entry: any) {
        const self = this;

        if (!entry) { return; }
        if (entry.SchoolGroupID == null) { return; }

        // Get a confirmation before proceeding
        let msg = `You are about to remove the following school group:<br /><br />` +
        `<strong>Group Name: </strong>${entry.Name}<br />` +
        `<strong>Schools: </strong><br /><ul>`;

        if (entry.Schools && !!entry.Schools.length) {
            entry.Schools.forEach(item => {
                msg += `<li>${(item.IsGroupLeader === true ? '<strong>' : '')}${item.LocationName} (${item.LocationNumber})` +
                `${(item.IsGroupLeader === true ? ' (Group Leader)' : '')}${(item.IsGroupLeader === true ? '</strong>' : '')}</li>`;
            });
        }

        msg += `</ul><br /><br />Once the school group update is removed, it will take effect immediately. ` +
        `Are you sure you wish to remove this school group as shown above?`;

        self.dialogService.OpenConfirmationDialog({
            TitleText: 'Confirmation Requested',
            MessageText: msg,
            OKText: 'Yes - Remove School Group',
            CancelText: 'No - CANCEL'
        }).pipe(takeUntil(self._subrelease)).subscribe(
            result => {
                if (result === true) {
                    // User has opted to proceed. Signal the system to remove this school group.
                    self.appService.RemoveSchoolGroup(self.User.AuthToken, self.User.CurrentRole, entry.SchoolGroupID)
                        .pipe(takeUntil(self._subrelease)).subscribe(groups => {
                                // Toast the success
                                self.splashService.SnackbarSuccess('School group removed successfully.');

                                // Update the list of known school groups
                                self.SchoolGroups = groups;
                                self.GroupsData.data = groups;

                        }, error => {
                            self.errorService.HandleServiceError(error);
                        }
                    );
                }
            }
        );
    }

    RemoveSchool(entrygp: FormGroup) {
        const self = this;

        if (!entrygp) { return; }

        const entry = entrygp.value;

        if (!entry.SchoolInfo) { return; }

        const schoolarr = <FormArray>self.GroupEntryForm.get('Schools');
        const schools = <any[]>schoolarr.value;

        // If this is a new (unsaved) entry, and no school was chosen, no need to confirm.
        if (entry.SchoolGroupSchoolID == null && !entry.LocationNumber) {
            const sidx = schools.findIndex(item => item.SortOrder === entry.SortOrder);

            schoolarr.removeAt(sidx);

            return;
        }

        // Is the selected entry the current group leader? If so, warn that it has to be changed first and exit out.
        if (entry.IsGroupLeader === true) {
            self.dialogService.OpenMessageBoxDialog({
                TitleText: 'Alert',
                MessageText: `The school you have selected to be removed is currently set as the school group leader. ` +
                `You cannot ${(entry.SchoolGroupSchoolID == null ? 'remove this school' : 'mark this school to be removed')} ` +
                `when it is set as the group leader.<br /><br />Please change the group leader first and then ` +
                `${(entry.SchoolGroupSchoolID == null ? 'remove this school' : 'mark this school to be removed')}.`,
                OKText: 'OK'
            }).pipe(takeUntil(self._subrelease)).subscribe();

            return;
        }

        // Is the selected entry one which is noted as having a nomination? If so, warn and exit out.
        if (entry.HasNomination === true) {
            self.dialogService.OpenMessageBoxDialog({
                TitleText: 'Alert',
                MessageText: `The school you have selected to be removed currently has an active nomination for the ` +
                `current school year. You cannot ${(entry.SchoolGroupSchoolID == null ?
                    'remove this scool' : 'mark this school to be removed')} when any active nominations are in place ` +
                    `for this school.`,
                OKText: 'OK'
            }).pipe(takeUntil(self._subrelease)).subscribe();

            return;
        }

        // Get a confirmation before proceeding.
        const msg = `You are about to ${(entry.SchoolGroupSchoolID == null ? 'remove the following school' :
            'mark the following school to be removed')} from this school group:<br /><br />` +
        `<strong>Location Name: </strong>${entry.LocationName}<br />` +
        `<strong>Location Number: </strong>${entry.LocationNumber}<br /><br />` +
        `Are you sure you wish to ${(entry.SchoolGroupSchoolID == null ? 'remove this school' : 'mark this school to be removed')}?`;

        self.dialogService.OpenConfirmationDialog({
            TitleText: 'Confirmation Requested',
            MessageText: msg,
            OKText: 'Yes - Remove',
            CancelText: 'No - CANCEL' })
            .pipe(takeUntil(self._subrelease)).subscribe(result => {
                if (result === true) {
                    // User has elected to proceed. If this is a group not yet in the system, just remove this entry from the array.
                    if (entry.SchoolGroupSchoolID == null) {
                        const sidx = schools.findIndex(item => item.LocationNumber === entry.LocationNumber);

                        schoolarr.removeAt(sidx);

                        return;
                    }

                    // This is an existing group. For now, mark it as pending delete.
                    entrygp.get('PendingRemove').setValue(true);
                }
            });
    }

    SetAsSchoolLeader(entrygp: FormGroup) {
        const self = this;

        if (!entrygp) { return; }

        const entry = entrygp.value;
        const schoolarr = <FormArray>self.GroupEntryForm.get('Schools');
        const schools = <any[]>schoolarr.value;

        const currentLeader = (schools || []).find(item => item.IsGroupLeader === true);

        // Get a confirmation before changing it, if this is an existing group only.
        (entry.SchoolGroupID == null ? of<boolean>(true).pipe(take(1)) :
            self.dialogService.OpenConfirmationDialog({
                TitleText: 'Confirmation Requested',
                MessageText: `You are about to change the school group leader location as follows:<br /><br />` +
                `<strong>Previous Group Leader: </strong>${currentLeader.LocationName} (${currentLeader.LocationNumber})<br />` +
                `<strong>New Group Leader: </strong>${entry.LocationName} (${entry.LocationNumber})<br /><br />` +
                `Once changed, nominations for this school group will reference the worksite / school ` +
                `location #${entry.LocationNumber}.<br /><br /><strong>Note: </strong>You must still add/update the school ` +
                `group to make this change active in the system.<br /><br />Are you sure you wish to continue?`,
                OKText: 'Yes - Update',
                CancelText: 'No - CANCEL'
            })).pipe(takeUntil(self._subrelease)).subscribe(result => {
                if (result === true) {
                    // User is okay. Run through all schools, and make sure that this entry location is the
                    // only one set as the group leader.
                    for (let i = 0, j = schoolarr.length; i < j; i++) {
                        const school = schoolarr.at(i);
                        const locNumber = school.get('LocationNumber').value;

                        school.get('IsGroupLeader').setValue((locNumber === entry.LocationNumber));
                    }
                }
            });
    }

    ShowGroupPanel(group?: SchoolGroupInfo) {
        const self = this;

        // Set up the formgroup to prepare for editing. If we were provided a school group, use those values to prefill the formgroup.
        if (group) {
            const patch = {
                SchoolGroupID: group.SchoolGroupID,
                OriginalName: group.Name,
                Name: group.Name,
                SchoolYear: self.CurrentSchoolYear
            };

            // Patch the values
            self.GroupEntryForm.patchValue(patch);

            // Setup the form array replacement
            const sgparr = <FormGroup[]>[];

            if (group.Schools && !!group.Schools.length) {
                group.Schools.forEach(sch => {
                    const thissch = self.AvailableSchools.find(s => s.SchoolNumber === sch.LocationNumber);

                    const schgp = self.fb.group({
                        SchoolGroupSchoolID: [sch.SchoolGroupSchoolID],
                        LocationNumber: [sch.LocationNumber],
                        LocationName: [sch.LocationName],
                        SortOrder: [sch.SortOrder],
                        IsGroupLeader: [sch.IsGroupLeader],
                        HasNomination: [sch.HasNomination],
                        PendingRemove: [false],
                        SchoolInfo: [thissch]
                    });

                    sgparr.push(schgp);
                });
            }

            self.GroupEntryForm.setControl('Schools', self.fb.array(sgparr));
        } else {
            // Clear the form
            self.GroupEntryForm.reset();

            self.GroupEntryForm.setControl('Schools', self.fb.array([]));
        }

        self.GroupEntryForm.updateValueAndValidity({emitEvent : false });

        self.ShowingGroupPanel = true;
    }

    UpdateGroup() {
        const self = this;

        if (!self.GroupEntryForm || !self.GroupEntryForm.valid) { return; }

        const form = self.GroupEntryForm.value;

        // Get a confirmation before proceeding
        let msg = `You are about to update the following school group:<br /><br />` +
        `<strong>Group Name: </strong>${form.Name}<br /><strong>Schools: </strong><br /><ul>`;

        if (form.Schools && !!form.Schools.length) {
            form.Schools.filter(pditem => pditem.PendingRemove !== true)
                .forEach(item => {
                    msg += `<li>${(item.IsGroupLeader === true ? '<strong>' : '')}` +
                    `${item.LocationName} (${item.LocationNumber})` +
                    `${(item.IsGroupLeader === true ? ' (Group Leader)' : '')}` +
                    `${(item.IsGroupLeader === true ? '</strong>' : '')}</li>`;
                });
        }

        msg += `</ul><br /><br />Once the school group update is processed, it will take ` +
        `effect immediately. Are you sure you wish to update this school group as shown above?`;

        self.dialogService.OpenConfirmationDialog({
            TitleText: 'Confirmation Requested',
            MessageText: msg,
            OKText: 'Yes - Update School Group',
            CancelText: 'No - CANCEL'
        }).pipe(takeUntil(self._subrelease)).subscribe(result => {
            if (result === true) {
                // Update the school group as requested.
                // Put together the packet needed to add the new group
                const dbPacket = {
                    Name: form.Name,
                    SchoolYear: form.SchoolYear,
                    SchoolGroupID: form.SchoolGroupID,
                    Schools: form.Schools.filter(item => item.PendingRemove !== true)
                                .map(item => {
                                    return {
                                        LocationNumber: item.LocationNumber,
                                        IsGroupLeader: item.IsGroupLeader
                                    };
                                })
                };

                self.appService.UpdateSchoolGroup(self.userService.User.AuthToken,
                    self.userService.User.CurrentRole, self.AwardInfo.AwardID, dbPacket)
                    .pipe(takeUntil(self._subrelease)).subscribe(sgroups => {
                        // Toast the success
                        self.splashService.SnackbarSuccess('School group updated successfully.');

                        // Update the list of known school groups
                        self.SchoolGroups = sgroups;
                        self.GroupsData.data = sgroups;

                        self.GroupEntryForm.reset();
                        self.ShowingGroupPanel = false;
                }, error => self.errorService.HandleServiceError(error));
            }
        });
    }

    UpdateSchool(schgroup: FormGroup) {
        const self = this;

        if (!schgroup) { return; }

        const school = schgroup.value;

        if (!school.SchoolInfo) {
            schgroup.patchValue({
                IsGroupLeader: false,
                LocationName: null,
                LocationNumber: null,
                PendingRemove: false
            });
        } else {
            const patch = {
                LocationName: school.SchoolInfo.SchoolName,
                LocationNumber: school.SchoolInfo.SchoolNumber
            };

            schgroup.patchValue(patch);
        }
    }


}


export function SchoolGroupValidator(group: FormGroup): ValidationErrors {
    const value = group.value;

    if (!value.Name) {
        return {
            MissingGroupName: true
        };
    }

    if (!value.Schools || !value.Schools.length || value.Schools.length < 2) {
        return {
            NotEnoughSchools: true
        };
    }

    // Iterate all school entries to make sure they all have location numbers set. We also must
    // have exactly one group leader.
    let numLeaders = 0;
    let anyBad = false;

    value.Schools.forEach(sch => {
        if (!sch.LocationNumber) {
            anyBad = true;
            return;
        }

        if (sch.IsGroupLeader) { numLeaders++; }
    });

    if (anyBad) {
        return {
            MissingSchoolNumber: true
        };
    }

    if (numLeaders !== 1) {
        return {
            IncorrectNumberOfGroupLeaders: true
        };
    }

    return null;
}


